import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { DynamicFormModel } from 'models/AiDataModels';
import { CaseModel } from 'models/CaseModel';
import { ExamGridModel } from 'models/ExamGridModel';
import { FileModel } from 'models/FileModel';
import { PatientModel } from 'models/PatientModel';
import { TagModel } from 'models/TagModel';
import { TaskModel } from 'models/TaskModel';

import { ApiRouteService } from 'core/api/services';
import { NotificationsService } from 'core/notifications';
import { Button, Card, Dropdown, ProgressSpinner } from 'core/ui';

import { useApiClient, useApiQuery } from 'features/api';
import { AuthenticationScheme, useAuthentication } from 'features/auth';
import { useSignalRRefresh } from 'features/auth/hooks/useSignalRRefresh';
import { useCasesQuery } from 'features/case/hooks/useCasesQuery';
import { FileViewerPopup } from 'features/file/fragments/FileViewerPopup';
import { ShareType } from 'features/patient/constants';
import { processTagData } from 'features/patient/services/tagHelperUtils';
import { useMergedTags } from 'features/tags/hooks/useMergedTags';
import { QRScanModal } from 'features/upload-exams/fragments/QRScanModal';

import { getTagsForFile } from '../services/file-category-helper';
import ActivityTimeline, { TimelineItem as ActivityTimelineItem } from './ActivityTimeline';
import { Category } from './PatientFiles.types';
import { PatientFilesListView } from './PatientFilesListView';

type CaseOption = { name: string; value: number | null };
const ALL_FILES_CASE: CaseOption = { name: 'All Files', value: null };

const FormsListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormItemCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.palette.grayscale[3]};
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.palette.grayscale[0]};
`;

const FormItemHeader = styled.div`
  padding: 0.5rem 1rem;
  background-color: ${({ theme }) => theme.colors.palette.grayscale[2]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.palette.grayscale[3]};
  font-size: 0.9rem;
`;

const FormFieldsList = styled.ul`
  list-style: none;
  padding: 1rem;
  margin: 0;
  font-size: 0.85rem;
  li {
    margin-bottom: 0.3rem;
  }
`;

const StyledDashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  height: calc(100vh - 100px);
`;

const StyledMainContent = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr;
  gap: 20px;
  flex-grow: 1;
  overflow: hidden;
`;

const StyledLeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
`;

const StyledRightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
  max-height: 100%;
`;

const StyledSectionCard = styled(Card)<{ fullHeight?: boolean }>`
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  ${({ fullHeight }) => fullHeight && `height: 100%;`}
`;

const StyledCardHeader = styled.div`
  font-size: 18px;
  font-weight: 600;
  padding: 15px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.palette.grayscale[3]};
  background-color: ${({ theme }) => theme.colors.palette.grayscale[1]};
`;

const StyledCardContent = styled.div`
  padding: 20px;
  flex-grow: 1;
  overflow-y: auto;
`;

const StyledFilterContainer = styled.div`
  margin-bottom: 20px;
  display: flex;
  justify-content: flex-start;
`;

const StyledEmptyMessage = styled.div`
  text-align: center;
  padding: 30px;
  color: ${({ theme }) => theme.colors.palette.grayscale[6]};
  font-style: italic;
`;

interface PatientDashboardProps {
  patientId: number | null;
  gridData: ExamGridModel[];
  patient: PatientModel | null;
  onEditPatient?: () => void;
}

const PatientDashboard: React.FC<PatientDashboardProps> = ({ patientId, gridData, patient, onEditPatient }) => {
  const apiClient = useApiClient();
  const { activeScheme } = useAuthentication();

  const [files, setFiles] = useState<FileModel[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [tasks, setTasks] = useState<TaskModel[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [patientForms, setPatientForms] = useState<DynamicFormModel[]>([]);
  const [isLoadingPatientForms, setIsLoadingPatientForms] = useState<boolean>(false);

  // QR Code modal state for case-based mobile uploads
  const [isQrModalVisible, setIsQrModalVisible] = useState(false);
  const [qrLinkId, setQrLinkId] = useState<string>('');
  const [isGeneratingQrCode, setIsGeneratingQrCode] = useState(false);

  const { data: cases, isLoading: isLoadingCases, error: casesError } = useCasesQuery(patientId ?? undefined);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(ALL_FILES_CASE.value);

  // Get tags data for mapping case tag names to IDs
  const { data: rawTags = [], isLoading: isLoadingTags } = useApiQuery({
    queryKey: ['tags', 'levels'],
    apiFn: apiClient.tagsClient.getLevelTags,
    params: [],
  });

  // Process tags data using centralized utility
  const {
    allHierarchicalTags: fetchedHierarchicalTags,
    allFlatTags: fetchedFlatTags,
    tagNameToIdMap: fetchedTagNameToIdMap,
    tagIdToTagMap: fetchedTagIdToTagMap,
  } = useMemo(() => {
    return processTagData(rawTags);
  }, [rawTags]);

  const { mergedHierarchicalTags, mergedFlatTags, mergedTagNameToIdMap, mergedTagIdToTagMap, isLoadingMergedTags } = useMergedTags({
    fetchedHierarchicalTags,
    fetchedTagNameToIdMap,
    fetchedTagIdToTagMap,
    isLoadingTags,
  });

  const MOCK_LUNG_CASE = useMemo(
    () => ({
      id: -1,
      name: 'Lung Case (Default)',
      patientId: patientId ?? -1,
      description: 'Default case for files related to Lungs',
      organ: 'Lungs',
      tags: [
        { id: -1, name: 'lung' },
        { id: -2, name: 'ct' },
        { id: -3, name: 'x-ray' },
        { id: -4, name: 'ventilator' },
        { id: -5, name: 'pathology' },
        { id: -6, name: 'chest x-ray' },
        { id: -7, name: 'report' },
        { id: -8, name: 'image' },
      ],
      files: null,
      exams: null,
      tasks: null,
      dateCreated: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
    }),
    [patientId],
  );

  const allAvailableCases = useMemo(() => {
    const realCases = cases || [];
    if (realCases.some((c) => c.id === MOCK_LUNG_CASE.id)) {
      return realCases;
    }
    return [...realCases, MOCK_LUNG_CASE];
  }, [cases, MOCK_LUNG_CASE]);

  const caseOptions = useMemo<CaseOption[]>(() => {
    const options = allAvailableCases.map((c) => ({ name: c.name, value: c.id }));
    return [ALL_FILES_CASE, ...options];
  }, [allAvailableCases]);

  const [viewFileState, setViewFileState] = useState<{
    isOpen: boolean;
    files: FileModel[];
    selectedFile: FileModel | null;
    title: string;
    examId?: number;
  }>({ isOpen: false, files: [], selectedFile: null, title: '' });

  useEffect(() => {
    const fetchFiles = async () => {
      if (patientId) {
        setLoadingFiles(true);
        try {
          const response = await apiClient.filesClient.getFilesByPatientId(patientId);
          setFiles(response);
        } catch (error) {
          console.error('Error fetching files:', error);
          setFiles([]);
        } finally {
          setLoadingFiles(false);
        }
      }
    };
    fetchFiles();
  }, [patientId, apiClient.filesClient]);

  // Use SignalR to refresh files when uploads occur
  const fetchFiles = useCallback(async () => {
    if (patientId) {
      setLoadingFiles(true);
      try {
        const response = await apiClient.filesClient.getFilesByPatientId(patientId);
        setFiles(response);
      } catch (error) {
        console.error('Error fetching files:', error);
        setFiles([]);
      } finally {
        setLoadingFiles(false);
      }
    }
  }, [patientId, apiClient.filesClient]);

  // Use SignalR to refresh files when uploads occur
  useSignalRRefresh({
    patientId: typeof patientId === 'number' ? patientId : undefined,
    onPatientFileUploaded: fetchFiles,
  });

  useEffect(() => {
    const fetchTasks = async () => {
      if (selectedCaseId && selectedCaseId !== ALL_FILES_CASE.value) {
        setLoadingTasks(true);
        try {
          const response = await apiClient.caseClient.getCaseTasks(selectedCaseId, { authMode: 'msal-required' });
          setTasks(response || []);
        } catch (error) {
          console.error(`Error fetching tasks for case ${selectedCaseId}:`, error);
          setTasks([]);
        } finally {
          setLoadingTasks(false);
        }
      } else {
        setTasks([]);
      }
    };

    if (patientId) {
      fetchTasks();
    }
  }, [patientId, selectedCaseId, apiClient.caseClient]);

  useEffect(() => {
    if (patientId) {
      const fetchPatientForms = async () => {
        setIsLoadingPatientForms(true);
        try {
          const options = await apiClient.httpClient.createStandardOptions('GET', null);
          const url = `${ApiRouteService.getCompumedApiBaseRoute()}/api/parseddata/patients/${patientId}/form-submissions`;
          const response = await apiClient.httpClient.fetchAndParse<DynamicFormModel[]>(fetch(url, options));
          setPatientForms(response || []);
        } catch (error) {
          console.error('Error fetching patient form submissions:', error);
          setPatientForms([]);
        } finally {
          setIsLoadingPatientForms(false);
        }
      };
      fetchPatientForms();
    } else {
      setPatientForms([]);
    }
  }, [patientId, apiClient.httpClient]);

  const handleCaseChange = useCallback((event: DropDownListChangeEvent) => {
    const value = event.target.value;
    setSelectedCaseId(typeof value === 'number' ? value : ALL_FILES_CASE.value);
  }, []);

  const getFilesForCase = useCallback(
    (caseItem: CaseModel): FileModel[] => {
      const caseTags = Array.isArray(caseItem.tags) ? caseItem.tags.map((t: TagModel) => String(t.name).toLowerCase()) : [];
      if (caseTags.length === 0 || !Array.isArray(files) || files.length === 0) {
        return [];
      }
      return files.filter((file) => {
        const fileTags = getTagsForFile(file, gridData);
        return Array.isArray(fileTags) && fileTags.some((fileTag) => caseTags.includes(fileTag));
      });
    },
    [files, gridData],
  );

  const casesForListView = useMemo(() => {
    let filteredCases: CaseModel[];
    if (selectedCaseId === ALL_FILES_CASE.value) {
      filteredCases = allAvailableCases;
    } else {
      const selected = allAvailableCases.find((c) => c.id === selectedCaseId);
      filteredCases = selected ? [selected] : [];
    }
    return filteredCases.map((caseItem) => ({
      ...caseItem,
      files: getFilesForCase(caseItem),
    }));
  }, [selectedCaseId, allAvailableCases, getFilesForCase]);

  const filesToDisplay = useMemo(() => {
    if (selectedCaseId === ALL_FILES_CASE.value) {
      return files;
    } else {
      const selectedCaseData = casesForListView.find((c) => c.id === selectedCaseId);
      return selectedCaseData?.files || [];
    }
  }, [selectedCaseId, files, casesForListView]);

  const handleFileSelect = useCallback((file: FileModel, category: Category) => {
    console.log('File selected in Dashboard:', file.fileName, 'Category:', category.displayName);
  }, []);

  const handleViewFile = useCallback((file: FileModel, category: Category) => {
    setViewFileState({
      isOpen: true,
      files: category.files || [file],
      selectedFile: file,
      title: `${category.displayName} - ${file.fileName || 'File'}`,
    });
  }, []);

  const handleActivityTimelineItemClick = (item: ActivityTimelineItem) => {
    if (item.type === 'exam' && item.exam) {
      if (item.exam.id) {
        setViewFileState({
          isOpen: true,
          files: [],
          selectedFile: null,
          title: `${item.exam.serviceDescription ?? 'Exam'} ${item.exam.description ? `- ${item.exam.description}` : ''}`,
          examId: item.exam.id,
        });
      }
    } else if (item.type === 'file' && item.file) {
      setViewFileState({
        isOpen: true,
        files: [item.file],
        selectedFile: item.file,
        title: item.file.fileName || 'File Viewer',
      });
    } else if (item.type === 'comment' && item.comment) {
      console.log('Comment clicked:', item.comment);
    } else if (item.type === 'task' && item.task) {
      console.log('Task clicked:', item.task);
    }
  };

  // QR Code generation for case-based mobile uploads
  const handleGenerateQrCode = useCallback(async () => {
    if (!patient) return;

    setIsGeneratingQrCode(true);
    try {
      const linkId = crypto.randomUUID();
      const expireOn = dayjs().add(1, 'day').format('YYYY-MM-DDTHH:mm:ss');

      // Get the selected case and map its tags to actual tag IDs
      let message = 'Patient File Upload';

      if (selectedCaseId && selectedCaseId !== ALL_FILES_CASE.value) {
        const selectedCase = allAvailableCases.find((c) => c.id === selectedCaseId);
        if (selectedCase?.tags && Array.isArray(selectedCase.tags) && mergedTagNameToIdMap.size > 0) {
          // Map case tag names to actual tag IDs
          const mappedTagIds = selectedCase.tags
            .map((tag: TagModel) => {
              const tagName = tag.name.toLowerCase();
              return mergedTagNameToIdMap.get(tagName);
            })
            .filter((id): id is number => id !== undefined);

          if (mappedTagIds.length > 0) {
            if (mappedTagIds.length === 1) {
              message = `TAG_ID:${mappedTagIds[0]}`;
            } else {
              message = `TAG_IDS:${mappedTagIds.join(',')}`;
            }
          } else {
            // Fallback: encode case name in message for context
            message = `CASE:${selectedCase.name}`;
          }
        }
      }

      await apiClient.studyShare.addStudyShare(
        {
          id: 0,
          linkId: linkId,
          email: null,
          sharePassword: null,
          shareType: ShareType.Link,
          sharePatientId: patient.id,
          studyShareExams: [],
          expireOn: expireOn,
          dateCreated: '',
          message: message,
          bypassSplashPage: true,
        },
        activeScheme === AuthenticationScheme.OIDC ? 'msal-required' : 'share-required',
      );

      setQrLinkId(linkId);
      setIsQrModalVisible(true);

      NotificationsService.displaySuccess('QR code generated successfully! Show this to the mobile device.');
    } catch (error) {
      console.error('Failed to generate QR code:', error);
      NotificationsService.displayError('Failed to generate QR code. Please try again.');
    } finally {
      setIsGeneratingQrCode(false);
    }
  }, [patient, apiClient, activeScheme, selectedCaseId, allAvailableCases, mergedTagNameToIdMap]);

  const handleQrModalClose = useCallback(() => {
    setIsQrModalVisible(false);
    setQrLinkId('');
  }, []);

  // Generate upload context for QR modal based on selected case
  const uploadContext = useMemo(() => {
    if (selectedCaseId && selectedCaseId !== ALL_FILES_CASE.value) {
      const selectedCase = allAvailableCases.find((c) => c.id === selectedCaseId);
      if (selectedCase) {
        // For case-based uploads, we can provide context via the case name
        // The actual tag mapping is handled in the share message
        return {
          currentPath: `case-${selectedCase.name.toLowerCase().replace(/\s+/g, '-')}`,
        };
      }
    }
    return {};
  }, [selectedCaseId, allAvailableCases]);

  const isLoading = loadingFiles || isLoadingCases || loadingTasks || isLoadingPatientForms;

  return (
    <StyledDashboardContainer>
      <StyledMainContent>
        <StyledLeftColumn>
          <StyledFilterContainer>
            <Dropdown
              data={caseOptions}
              textField="name"
              dataItemKey="value"
              valueField="value"
              value={selectedCaseId}
              onChange={handleCaseChange}
              filterable={false}
              isForPrimitiveValues={true}
              popupSettings={{ className: 'compact-dropdown' }}
              title="Filter by Case"
              disabled={isLoading}
              style={{ width: '250px' }}
            />
            <Button
              onClick={handleGenerateQrCode}
              disabled={isGeneratingQrCode || !patient}
              style={{ marginLeft: '10px' }}
              title="Generate QR code for mobile uploads to this case"
            >
              {isGeneratingQrCode ? 'Generating...' : 'QR Upload'}
            </Button>
          </StyledFilterContainer>

          <StyledSectionCard>
            <StyledCardHeader>Cases and Related Files</StyledCardHeader>
            <StyledCardContent>
              <PatientFilesListView
                files={filesToDisplay}
                gridData={gridData}
                isLoading={isLoading}
                onFileSelect={handleFileSelect}
                onViewFile={handleViewFile}
              />
            </StyledCardContent>
          </StyledSectionCard>

          <StyledSectionCard>
            <StyledCardHeader>All Patient Form Submissions</StyledCardHeader>
            <StyledCardContent>
              {isLoadingPatientForms ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
                  <ProgressSpinner />
                </div>
              ) : patientForms.length > 0 ? (
                <FormsListContainer>
                  {patientForms.map((formSubmission) => (
                    <FormItemCard key={formSubmission.id}>
                      <FormItemHeader>
                        <strong>{formSubmission.form.name}</strong> (Submitted: {dayjs(formSubmission.dateSubmitted).format('YYYY-MM-DD HH:mm')})
                      </FormItemHeader>
                      <FormFieldsList>
                        {formSubmission.fields.map((field) => (
                          <li key={field.name}>
                            {field.label}: {field.value} {field.unit || ''}
                          </li>
                        ))}
                      </FormFieldsList>
                    </FormItemCard>
                  ))}
                </FormsListContainer>
              ) : (
                <StyledEmptyMessage>No form submissions found for this patient.</StyledEmptyMessage>
              )}
            </StyledCardContent>
          </StyledSectionCard>
        </StyledLeftColumn>

        <StyledRightColumn>
          <StyledSectionCard fullHeight>
            <StyledCardHeader>Activity Timeline</StyledCardHeader>
            <StyledCardContent style={{ flexGrow: 1, overflowY: 'auto' }}>
              {patientId ? (
                <ActivityTimeline
                  patientId={patientId}
                  caseId={selectedCaseId !== ALL_FILES_CASE.value ? selectedCaseId : null}
                  exams={gridData}
                  files={files}
                  tasks={tasks}
                  onItemClick={handleActivityTimelineItemClick}
                />
              ) : (
                <StyledEmptyMessage>Select a patient to see their activity.</StyledEmptyMessage>
              )}
            </StyledCardContent>
          </StyledSectionCard>
        </StyledRightColumn>
      </StyledMainContent>

      <FileViewerPopup
        isOpen={viewFileState.isOpen}
        files={viewFileState.files}
        selectedFile={viewFileState.selectedFile}
        title={viewFileState.title}
        examId={viewFileState.examId}
        onClose={() => setViewFileState({ isOpen: false, files: [], selectedFile: null, title: '' })}
      />

      {/* QR Code Modal for case-based mobile uploads */}
      {isQrModalVisible && qrLinkId && (
        <QRScanModal linkId={qrLinkId} onClose={handleQrModalClose} show={isQrModalVisible} examId={null} uploadContext={uploadContext} />
      )}
    </StyledDashboardContainer>
  );
};

export default PatientDashboard;
